import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateReminderScheduleDto, UpdateReminderScheduleDto } from '@trellis/api'
import type { ReminderEmailStage, UpdateInvoiceReminder } from '@trellis/shared'

import {
  createReminderSchedule,
  deleteReminderSchedule,
  getDefaultReminderSchedule,
  getReminderEmailPreview,
  getReminderScheduleLimit,
  listInvoiceReminders,
  listReminderSchedules,
  stopInvoiceReminders,
  updateInvoiceReminder,
  updateReminderSchedule,
  type InvoiceRemindersListParams,
  type ReminderScheduleScope,
} from '@/requests/reminders'

export const REMINDER_SCHEDULES_QUERY_KEY = ['reminder-schedules'] as const
export const INVOICE_REMINDERS_QUERY_KEY = ['invoice-reminders'] as const
export const REMINDER_RATE_LIMITED_ERROR_CODE = 'REMINDER_RATE_LIMITED'

/** Global schedules by default; `scope: 'client'` lists every client's own schedule. */
export function useReminderSchedulesQuery({
  scope = 'global',
  enabled = true,
}: { scope?: ReminderScheduleScope; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...REMINDER_SCHEDULES_QUERY_KEY, 'list', scope],
    queryFn: () => listReminderSchedules(scope),
    enabled,
  })
}

/** Client responses embed the client's applied schedule, so refresh them too. */
async function invalidateScheduleViews(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: REMINDER_SCHEDULES_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: ['clients'] }),
  ])
}

/**
 * The organisation's cap on reminder schedules (client schedules included), set from the
 * console app, and how many it has now.
 */
export function useReminderScheduleLimitQuery({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...REMINDER_SCHEDULES_QUERY_KEY, 'limit'],
    queryFn: () => getReminderScheduleLimit(),
    enabled,
  })
}

export function useDefaultReminderScheduleQuery({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...REMINDER_SCHEDULES_QUERY_KEY, 'default'],
    queryFn: () => getDefaultReminderSchedule(),
    enabled,
  })
}

export function useReminderEmailPreviewQuery(
  stage: ReminderEmailStage,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...REMINDER_SCHEDULES_QUERY_KEY, 'email-preview', stage],
    queryFn: () => getReminderEmailPreview(stage),
    enabled,
    staleTime: Infinity,
  })
}

export function useCreateReminderScheduleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateReminderScheduleDto) => {
      const result = await createReminderSchedule(payload)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: () => invalidateScheduleViews(queryClient),
  })
}

export function useUpdateReminderScheduleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      scheduleId,
      payload,
    }: {
      scheduleId: string
      payload: UpdateReminderScheduleDto
    }) => {
      const result = await updateReminderSchedule(scheduleId, payload)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: () => invalidateScheduleViews(queryClient),
  })
}

export function useDeleteReminderScheduleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const result = await deleteReminderSchedule(scheduleId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: () => invalidateScheduleViews(queryClient),
  })
}

export function useInvoiceRemindersQuery(
  params: InvoiceRemindersListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...INVOICE_REMINDERS_QUERY_KEY, params],
    queryFn: () => listInvoiceReminders(params),
    enabled,
  })
}

/** Reminder changes move the invoices table chip and the invoice page, so refresh both. */
async function invalidateReminderViews(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: INVOICE_REMINDERS_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  ])
}

export function useUpdateInvoiceReminderMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      reminderId,
      status,
    }: {
      reminderId: string
      status: UpdateInvoiceReminder['status']
    }) => {
      const result = await updateInvoiceReminder(reminderId, { status })
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: () => invalidateReminderViews(queryClient),
  })
}

export function useStopInvoiceRemindersMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const result = await stopInvoiceReminders(invoiceId)
      if (result.error) throw new Error(result.error.message)
      return result.data
    },
    onSuccess: () => invalidateReminderViews(queryClient),
  })
}

/** Restores several dismissed reminders at once, e.g. undoing Stop all. */
export function useRestoreInvoiceRemindersMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reminderIds: string[]) => {
      const results = await Promise.all(
        reminderIds.map((reminderId) =>
          updateInvoiceReminder(reminderId, { status: 'SCHEDULED' }),
        ),
      )
      const failed = results.find((result) => result.error)
      if (failed?.error) throw new Error(failed.error.message)
    },
    onSettled: () => invalidateReminderViews(queryClient),
  })
}
